import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { 
    Plus, 
    FileText, 
    Settings, 
    Download,
    ChevronLeft,
    Menu,
    Trash2,
    Copy,
    MoreHorizontal,
    X
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from './ui/alert-dialog';
import { useTheme } from '../context/ThemeContext';

const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text || '';
    return text.substring(0, maxLength).trim() + '...';
};

export const IdeaList = ({
    ideas,
    selectedId,
    onSelect,
    onNew,
    onDelete,
    onDuplicate,
    onOpenSettings,
    onOpenExport,
    collapsed,
    onToggleCollapse,
    onBackToLanding,
    mobileOpen,
    onMobileClose,
}) => {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [ideaToDelete, setIdeaToDelete] = useState(null);
    const { theme, toggleTheme } = useTheme();

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const stored = window.localStorage.getItem('ideavault-sidebar-collapsed');
        if (stored !== null) {
            const isCollapsed = stored === 'true';
            if (isCollapsed !== collapsed) {
                onToggleCollapse();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem('ideavault-sidebar-collapsed', String(collapsed));
    }, [collapsed]);
    
    const handleDeleteClick = (e, idea) => {
        e.stopPropagation();
        setIdeaToDelete(idea);
        setDeleteDialogOpen(true);
    };
    
    const handleConfirmDelete = () => {
        if (ideaToDelete) onDelete(ideaToDelete.id);
        setDeleteDialogOpen(false);
        setIdeaToDelete(null);
    };
    
    const handleDuplicate = (e, id) => {
        e.stopPropagation();
        onDuplicate(id);
    };

    const handleIdeaSelect = (id) => {
        onSelect(id);
        if (onMobileClose) onMobileClose();
    };
    
    const showMobileOverlay = mobileOpen;

    if (collapsed && !mobileOpen) {
        return (
            <div className="hidden lg:flex h-full flex-col border-r border-border/60 sidebar-bg backdrop-blur-sm w-16">
                <div className="p-3 flex flex-col gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onToggleCollapse}
                        className="h-11 w-11 rounded-full hover:bg-accent"
                        data-testid="expand-sidebar-btn"
                    >
                        <Menu className="h-5 w-5 text-muted-foreground" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onNew}
                        className="h-11 w-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="new-idea-btn-collapsed"
                    >
                        <Plus className="h-5 w-5" />
                    </Button>
                </div>
            </div>
        );
    }

    const renderSidebarContent = () => (
        <div 
            className="h-full flex flex-col border-r border-border/60 sidebar-bg backdrop-blur-sm w-[280px] lg:w-72"
            data-testid="idea-list-sidebar"
        >
            {/* Header */}
            <div className="p-4 border-b border-border/60">
                <div className="flex items-center justify-between mb-4">
                    <button 
                        onClick={onBackToLanding}
                        className="flex items-center gap-2.5 group"
                    >
                        <div className="w-9 h-9 lg:w-8 lg:h-8 rounded-lg bg-card shadow-sm flex items-center justify-center group-hover:scale-105 transition-transform duration-150">
                            <svg className="w-5 h-5 text-indigo-400" fill="none" viewBox="0 0 48 48">
                                <path d="M42.1739 20.1739L27.8261 5.82609C29.1366 7.13663 28.3989 10.1876 26.2002 13.7654C24.8538 15.9564 22.9595 18.3449 20.6522 20.6522C18.3449 22.9595 15.9564 24.8538 13.7654 26.2002C10.1876 28.3989 7.13663 29.1366 5.82609 27.8261L20.1739 42.1739C21.4845 43.4845 24.5355 42.7467 28.1133 40.548C30.3042 39.2016 32.6927 37.3073 35 35C37.3073 32.6927 39.2016 30.3042 40.548 28.1133C42.7467 24.5355 43.4845 21.4845 42.1739 20.1739Z" fill="currentColor"/>
                            </svg>
                        </div>
                        <span className="text-lg font-semibold text-foreground group-hover:text-indigo-500 transition-colors duration-150">
                            IdeaVault
                        </span>
                    </button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onMobileClose || onToggleCollapse}
                        className="h-11 w-11 lg:h-8 lg:w-8 rounded-full hover:bg-accent"
                        data-testid="collapse-sidebar-btn"
                    >
                        <X className="h-5 w-5 lg:hidden text-muted-foreground" />
                        <ChevronLeft className="h-4 w-4 hidden lg:block text-muted-foreground" />
                    </Button>
                </div>
                <Button
                    onClick={() => { onNew(); if (onMobileClose) onMobileClose(); }}
                    className="w-full rounded-full h-11 font-medium bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/10"
                    data-testid="new-idea-btn"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    New Idea
                </Button>
            </div>
            
            {/* Ideas List */}
            <ScrollArea className="flex-1">
                <div className="p-2">
                    {ideas.length === 0 ? (
                        <div className="p-8 text-center">
                            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-accent flex items-center justify-center">
                                <FileText className="h-7 w-7 text-muted-foreground/50" />
                            </div>
                            <p className="text-sm font-medium text-foreground mb-1">No ideas yet</p>
                            <p className="text-xs text-muted-foreground">Click &quot;New Idea&quot; to start</p>
                        </div>
                    ) : (
                        ideas.map((idea, index) => (
                            <div
                                key={idea.id}
                                className="animate-fade-in"
                                style={{ animationDelay: `${index * 30}ms` }}
                            >
                                <DropdownMenu>
                                    <div
                                        onClick={() => handleIdeaSelect(idea.id)}
                                        className={`
                                            group relative p-3 rounded-xl cursor-pointer mb-1
                                            transition-all duration-150
                                            ${selectedId === idea.id 
                                                ? 'bg-card shadow-sm border border-border/60' 
                                                : 'hover:bg-accent/50'
                                            }
                                        `}
                                        data-testid={`idea-item-${idea.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-medium text-sm text-foreground truncate">
                                                    {idea.title || 'Untitled'}
                                                </h3>
                                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                                    {truncateText(idea.content)}
                                                </p>
                                                <p className="text-xs text-muted-foreground/60 mt-2">
                                                    {formatDate(idea.updatedAt)}
                                                </p>
                                            </div>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-11 w-11 lg:h-8 lg:w-8 rounded-full opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-150 hover:bg-accent"
                                                    onClick={(e) => e.stopPropagation()}
                                                    data-testid={`idea-menu-${idea.id}`}
                                                >
                                                    <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                        </div>
                                    </div>
                                    <DropdownMenuContent align="end" className="w-40 rounded-xl">
                                        <DropdownMenuItem 
                                            onClick={(e) => handleDuplicate(e, idea.id)}
                                            className="rounded-lg h-11 lg:h-9"
                                            data-testid={`duplicate-idea-${idea.id}`}
                                        >
                                            <Copy className="h-4 w-4 mr-2" />
                                            Duplicate
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                            onClick={(e) => handleDeleteClick(e, idea)}
                                            className="text-destructive focus:text-destructive focus:bg-destructive/10 rounded-lg h-11 lg:h-9"
                                            data-testid={`delete-idea-${idea.id}`}
                                        >
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            Delete
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        ))
                    )}
                </div>
            </ScrollArea>
            
            {/* Footer */}
            <div className="p-3 border-t border-border/60">
                <div className="flex gap-2 mb-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onOpenExport}
                        className="flex-1 rounded-full h-11 lg:h-9 text-xs text-muted-foreground hover:text-foreground hover:bg-accent"
                        data-testid="export-btn"
                    >
                        <Download className="h-4 w-4 mr-1.5" />
                        Export
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onOpenSettings}
                        className="flex-1 rounded-full h-11 lg:h-9 text-xs text-muted-foreground hover:text-foreground hover:bg-accent"
                        data-testid="settings-btn"
                    >
                        <Settings className="h-4 w-4 mr-1.5" />
                        Settings
                    </Button>
                </div>
                <div className="h-9" />
            </div>
        </div>
    );
    
    return (
        <>
            {showMobileOverlay && (
                <div 
                    className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-200"
                    onClick={onMobileClose}
                />
            )}
            
            <div className={`
                fixed inset-y-0 left-0 z-50 lg:hidden
                transform transition-transform duration-200 ease-out
                ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
                {renderSidebarContent()}
            </div>
            
            <div className="hidden lg:block">
                {renderSidebarContent()}
            </div>
            
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent className="rounded-2xl max-w-[90vw] sm:max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-light">
                            Delete this idea?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete &quot;{ideaToDelete?.title || 'Untitled'}&quot;. 
                            This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2 sm:gap-0">
                        <AlertDialogCancel className="rounded-full h-11">Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleConfirmDelete}
                            className="rounded-full h-11 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                            data-testid="confirm-delete-btn"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
